import { itemMatchesQuery, normalizeLimit, searchStrings } from "./search-helpers.js";
import { groupItems } from "./group-items.js";

// Fuzzy mode uses two matchers behind a gate:
// an exact word-prefix pass runs first, and only when it matches nothing does
// the fuse.js edit-distance pass (typo tolerance, re-ranked by deterministic
// ordered-word/first-letter boosts) supply results. The gate is what keeps a
// query like "cough" on its substring hits (Whooping Cough, Coughlin) instead
// of surfacing fuzzy noise (Courtney, Suicidal Thoughts) beside them.
// Matches come back best-first, then bucket under their section headers. By
// default sections follow the source (API `doctor_finder_omnisearch_result_types`)
// order carried by `items`, matching the idle list; relevance orders the rows
// within a section.

// fuse ignores matches shorter than this; a shorter query falls back to substring
// filtering so a single character still lists matches.
export const MIN_FUZZY_QUERY_LENGTH = 2;

// fuse.js scores 0 (perfect) → 1 (none). Entries are one-per-search-string
// (`text` is one of the item's `searchStrings` — see `searchEntries` in
// index.js), so every score is a raw single-string score, matching how the
// legacy autocomplete scored `matchValue`.
// Legacy score thresholds (autocomplete.coffee option defaults): fuse admits
// scores below the initial cutoff so a mid-score match can still earn boosts;
// the final cutoff applies after boosting.
const FUZZY_INITIAL_CUTOFF_SCORE = 0.6; // pre-boost: discard before boost calculations
const FUZZY_FINAL_CUTOFF_SCORE = 0.4; // post-boost: discard from the ranked list
const FUZZY_EXACT_MATCH_THRESHOLD = 0.15; // lone sub-threshold top match collapses to a single result

export const FUSE_OPTIONS = {
  keys: ["text"],
  includeScore: true,
  ignoreLocation: true,
  threshold: FUZZY_INITIAL_CUTOFF_SCORE,
  minMatchCharLength: MIN_FUZZY_QUERY_LENGTH,
  shouldSort: false,
};

// Deterministic re-rank boosts subtracted from a fuse score (lower is better).
const ORDERED_WORD_BOOST = 0.35; // query words appear in order, on word boundaries
const FIRST_LETTER_BOOST = 0.1; // shares the query's first letter

// Rank via the exact gate (word-prefix matches on the item's search strings),
// falling back to boosted fuse results only when nothing matches exactly. Cap
// after ranking so the cap keeps the best matches, then group the capped
// matches under their section headers — in source order by default, or by each
// section's best-ranked row with `sectionOrder: "relevance"`. Pinned rows (an
// appended suggestion or phrase-search action row) skip the matching and
// section-grouping passes: they always render last, each run under its own
// header via `withPinnedHeaders`. An all-digit query never fuzzy-matches (an
// off-by-one digit is one edit away and would surface the wrong record); it
// matches by substring instead — unless `noSubstringFallback` routes it through
// the exact gate like any other query.
export function rankFuzzyItems(...args) {
  return rankFuzzyResults(...args).items;
}

/** Returns ranked rows and whether any ordinary item matched before capping. */
export function rankFuzzyResults(
  items,
  search,
  fuseResults,
  getItemTitle,
  maxResults,
  { sectionOrder = "source", noSubstringFallback = false } = {},
) {
  const query = search.trim();
  const pinned = items.filter((item) => item?.pinned);
  const limit = normalizeLimit(maxResults);
  const rankedLimit = limit === null ? null : Math.max(0, limit - pinned.length);

  let ranked;
  if (!noSubstringFallback && /^\d+$/.test(query)) {
    const needle = query.toLowerCase();
    ranked = items.filter(
      (item) => !item?.header && !item?.pinned && !item?.fallback && itemMatchesQuery(item, needle, getItemTitle),
    );
  } else {
    ranked = exactWordMatches(items, query, getItemTitle);
    if (!ranked.length) {
      ranked = fuzzyMatches(query, fuseResults);
    }
  }

  // `fallback: true` rows are an empty-state affordance (e.g. a selectable
  // no-results row): excluded from matching, rendered only when the query
  // ranks nothing else.
  const hasMatches = ranked.length > 0;
  if (!hasMatches) {
    ranked = items.filter((item) => item?.fallback);
  }

  const capped = rankedLimit === null ? ranked : ranked.slice(0, rankedLimit);
  const ordered =
    sectionOrder === "relevance" ? orderSectionsByRelevance(capped) : orderSectionsBySource(capped, items);
  return { items: [...groupItems(ordered, getItemTitle), ...withPinnedHeaders(pinned)], hasMatches };
}

// The exact gate (legacy `filterByExactMatch`): every query word must appear in
// order, each starting on a word boundary — "kelsey d" builds `\bkelsey.+\bd`.
// Unlike the ordered-word boost regex, every word here is prefix-matchable, not
// just the last. An item matches on any of its search strings; matches rank
// shortest-primary-string-first (all are equally exact, so the tightest match
// wins) — the title's length normally, `searchText`'s when an item carries one.
function exactWordMatches(items, query, getItemTitle) {
  const words = query.split(/\s+/).filter(Boolean);
  if (!words.length) {
    return [];
  }
  const pattern = new RegExp(words.map((word) => `\\b${escapeRegExp(word)}`).join(".+"), "i");
  const sortKey = (item) => searchStrings(item, getItemTitle)[0].length;

  return items
    .filter((item) => {
      if (!item || item.header || item.pinned || item.fallback) {
        return false;
      }
      return searchStrings(item, getItemTitle).some((text) => pattern.test(text));
    })
    .sort((a, b) => sortKey(a) - sortKey(b));
}

// The fuzzy fallback (legacy `filterByFuzzyMatch`): boost fuse's scores, drop
// anything still over the final cutoff, order best-first (ties keep the
// shortest matched string — the legacy `matchValue` length, so a keyword match
// ties on the keyword's length), and collapse to a single result when exactly
// one of the top two scores beats the exact-match threshold.
function fuzzyMatches(query, fuseResults) {
  const orderedWord = orderedWordRegex(query);
  const firstLetter = firstLetterRegex(query);

  // Boosts test the matched string itself (legacy tested `matchValue`); an item
  // matched through several of its strings keeps its best boosted score.
  const best = new Map();
  for (const { item: entry, score } of fuseResults) {
    if (score >= FUZZY_INITIAL_CUTOFF_SCORE) {
      continue;
    }
    let boosted = score;
    if (orderedWord.test(entry.text)) {
      boosted -= ORDERED_WORD_BOOST;
    }
    if (firstLetter.test(entry.text)) {
      boosted -= FIRST_LETTER_BOOST;
    }
    const existing = best.get(entry.item);
    if (!existing || boosted < existing.score) {
      best.set(entry.item, { item: entry.item, score: boosted, length: entry.text.length });
    }
  }

  let kept = [...best.values()].filter((result) => result.score < FUZZY_FINAL_CUTOFF_SCORE);
  kept.sort((a, b) => a.score - b.score || a.length - b.length);
  if (kept.slice(0, 2).filter((result) => result.score < FUZZY_EXACT_MATCH_THRESHOLD).length === 1) {
    kept = kept.slice(0, 1);
  }
  return kept.map(({ item }) => item);
}

// Order the ranked matches so their sections follow the source item order (the
// API `doctor_finder_omnisearch_result_types` sequence the server builds), rather
// than which section holds the single best match. Sort by each item's group
// position in `source`; the sort is stable, so relevance order is preserved within
// a section. Ungrouped rows sort last (grouped sections first).
function orderSectionsBySource(ranked, source) {
  const groupOrder = new Map();
  for (const item of source) {
    const group = item?.group;
    if (group != null && !groupOrder.has(group)) {
      groupOrder.set(group, groupOrder.size);
    }
  }
  const position = (item) => groupOrder.get(item?.group) ?? Number.MAX_SAFE_INTEGER;
  return [...ranked].sort((a, b) => position(a) - position(b));
}

// Order the ranked matches so their sections follow relevance: ungrouped rows
// lead (as a headerless block), then sections in first-appearance order of the
// ranked list — the section holding the best match comes first. The partition
// is stable, so relevance order is preserved within a section; `groupItems`
// buckets by first appearance downstream. Mirrors the legacy events
// omnisearch's rank-derived section order.
function orderSectionsByRelevance(ranked) {
  const ungrouped = ranked.filter((item) => item?.group == null);
  const grouped = ranked.filter((item) => item?.group != null);
  return [...ungrouped, ...grouped];
}

// Emit a `{ header }` row before each run of pinned rows sharing a `group`
// (group-less pinned rows render headerless). Header rows are labels, not
// results, so they do not count toward `maxResults`.
function withPinnedHeaders(pinned) {
  const rows = [];
  let currentGroup;
  for (const item of pinned) {
    const group = item?.group;
    if (group != null && group !== currentGroup) {
      rows.push({ header: group });
    }
    currentGroup = group;
    rows.push(item);
  }
  return rows;
}

// Ordered-word boost regex: non-final words are whole words (`\bword\b`), the
// final word need only start on a boundary (`\bword`) — "physical rehab" builds
// `\bphysical\b.+\brehab` and boosts "Physical Medicine and Rehabilitation".
function orderedWordRegex(query) {
  const words = query.split(/\s+/).filter(Boolean);
  if (!words.length) {
    return /(?!)/;
  }
  const parts = words.slice(0, -1).map((word) => `\\b${escapeRegExp(word)}\\b`);
  parts.push(`\\b${escapeRegExp(words[words.length - 1])}`);
  return new RegExp(parts.join(".+"), "i");
}

function firstLetterRegex(query) {
  return new RegExp(`^${escapeRegExp(query[0] ?? "")}`, "i");
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
